import React, { useState, useEffect } from 'react';
import { Input } from './ui/input';
import { X, Plus, Building, Info, Trash2 } from 'lucide-react';
import InlineDepartmentRow from './InlineDepartmentRow';
import { CommanServices } from '@/services/commanServices';

const LocationDepartmentManagement = ({ 
  selectedZone,
  onBack,
  managedLocationId, 
  associatedDepartments,
  setAssociatedDepartments,
  setZones,
  fetchLocationDepartmentCounts,
  updateZoneCounts,
  updateLocationCardCounts,
  fetchAssociatedDepartments,
  isLoadingDepartments,
  departmentSearchQuery, 
  setDepartmentSearchQuery,
  isAddingDepartment,
  addDepartmentToLocation,
  removeDepartmentFromLocation,
  updateDepartmentContact,
}) => {
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showAddDepartment, setShowAddDepartment] = useState(false);
  const [isRemoving, setIsRemoving] = useState(null); // Track which department is being removed

  // Add ESC key handler to close panel
  useEffect(() => {
    const handleKeyPress = (event) => {
      if (event.key === 'Escape') {
        onBack();
      }
    };

    document.addEventListener('keydown', handleKeyPress);
    return () => {
      document.removeEventListener('keydown', handleKeyPress);
    };
  }, [onBack]);
  
  // Debounced search function
  useEffect(() => {
    const searchDepartments = async () => {
      if (!departmentSearchQuery.trim()) {
        setSearchResults([]);
        return;
      }
      
      setIsSearching(true);
      try {
        const response = await CommanServices.departments();
        if (response.status) {
          const allDepartments = response.data || [];
          
          // Filter departments by name and exclude already associated ones
          const filtered = allDepartments.filter(dept => 
            dept.department_name.toLowerCase().includes(departmentSearchQuery.toLowerCase()) &&
            !associatedDepartments.some(assocDept => assocDept.id === dept.id)
          );
          
          setSearchResults(filtered);
        }
      } catch (error) {
        console.error('Error searching departments:', error);
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    };
    
    const timeoutId = setTimeout(searchDepartments, 300); // 300ms debounce
    return () => clearTimeout(timeoutId);
  }, [departmentSearchQuery, associatedDepartments]);

  // Enhanced remove function with proper error handling and retry
  const handleRemoveDepartment = async (departmentId, departmentName) => {
    const originalAssociatedDepartments = [...associatedDepartments];
    
    setIsRemoving(departmentId);
    
    try {
      // Call the main remove function which handles optimistic updates
      await removeDepartmentFromLocation(departmentId, departmentName);
      
      // Success toast is already handled in removeDepartmentFromLocation
      
    } catch (error) {
      console.error('Error removing department:', error);
      
      // Show error with retry option
      const shouldRetry = confirm(`❌ Failed to remove ${departmentName} from ${selectedZone.zone_name}.\n\nNetwork error occurred. Would you like to retry?`);
      
      if (shouldRetry) {
        // Retry the operation
        setTimeout(() => {
          handleRemoveDepartment(departmentId, departmentName);
        }, 1000);
        return; // Don't clear the loading state yet
      } else {
        // User declined retry - show simple error message
        alert(`❌ ${departmentName} removal was not completed. Please try again later.`);
      }
    } finally {
      setIsRemoving(null);
    }
  };

  // PROPER FIX: Single source of truth Primary toggle handler with versioning
  const handleTogglePrimary = async (deptId, isPrimary) => {
    console.log('🔄 TOGGLE: Starting primary toggle for', deptId, 'isPrimary:', isPrimary);
    
    // 1. Optimistic update - immediate UI feedback for panel only
    const originalDepts = [...associatedDepartments];
    const optimisticDepts = originalDepts.map(d => 
      d.id === deptId 
        ? { ...d, relationship_type: isPrimary ? 'primary' : 'secondary' }
        : d
    );
    
    // Apply optimistic updates to panel immediately
    setAssociatedDepartments(optimisticDepts);
    
    try {
      // 2. PATCH the authoritative
      const payload =  { relationship_type: isPrimary ? 'primary' : 'secondary' }
      
      const response = await CommanServices.updatelocationsdepartmentsbyId(selectedZone.id, deptId, payload);
      
      
      // 3. SINGLE SOURCE OF TRUTH: Refetch authoritative department data
      console.log('🔄 TOGGLE: Refetching authoritative departments...');
      const freshResponse = await CommanServices.getLocationDepartmentCounts(selectedZone.id);
      if (freshResponse.status) {
        const freshDepartments = freshResponse.data || [];
        
        
        // 4. Update panel with authoritative data
        setAssociatedDepartments(freshDepartments);
        
        // 5. CRITICAL: Update location card with versioned approach (SINGLE AFFECTED CARD)
        const totalCount = freshDepartments.length;
        const primaryCount = freshDepartments.filter(dept => dept.relationship_type === 'primary').length;
        const updateVersion = Date.now();
        
        console.debug(`[zones:toggle] Updating single card for location ${selectedZone.id.substring(0, 8)}... dept:${totalCount} primary:${primaryCount} v:${updateVersion}`);
        
        // Update only the affected location card
        setZones(prevZones => 
          prevZones.map(zone => {
            if (zone.id === selectedZone.id) {
              return {
                ...zone,
                departmentCount: totalCount,
                primaryDepartmentCount: primaryCount,
                linksVersion: updateVersion
              };
            }
            return zone; // Other cards unchanged
          })
        );
        
        // 6. Emit event with specific change information
        const event = new CustomEvent('location.departments.changed', {
          detail: {
            location_id: selectedZone.id,
            department_id: deptId,
            change: 'toggle',
            relationship_type: isPrimary ? 'primary' : 'secondary',
            total_departments: totalCount,
            primary_departments: primaryCount,
            version: updateVersion,
            source: 'TOGGLE_PRIMARY'
          }
        });
        document.dispatchEvent(event);
        
        console.log(`[event] location.departments.changed {location_id: ${selectedZone.id.substring(0, 8)}..., change: toggle}`);
        
      }
      
    } catch (error) {
      console.error('❌ TOGGLE: PATCH failed, reverting:', error);
      
      // 7. Rollback optimistic changes on error
      setAssociatedDepartments(originalDepts);
      
      // User-friendly error message
      const deptName = originalDepts.find(d => d.id === deptId)?.department_name || 'department';
      alert(`Failed to update ${deptName} to ${isPrimary ? 'Primary' : 'Secondary'}: ${error.response?.data?.detail || error.message}`);
    }
  };
  
  return (
    <div className="h-full flex flex-col">
      {/* Header with X close button in top-right */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-semibold text-gray-900">Associated Departments</h2>
              <div className="group relative inline-block">
                <Info 
                  className="w-4 h-4 text-blue-500 hover:text-blue-700 cursor-pointer inline-block" 
                  style={{ display: 'inline-block', visibility: 'visible' }}
                />
                <div className="absolute left-0 top-6 w-80 p-3 bg-black text-white text-xs rounded-lg shadow-xl opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-[999999]">
                  <div className="space-y-2">
                    <div>
                      <span className="font-semibold text-blue-300">Primary:</span>
                      <span className="ml-1">If an incident/event happens in this Location, the Primary Department (leaders + employees) will receive notifications.</span>
                    </div>
                    <div>
                      <span className="font-semibold text-orange-300">Secondary:</span>
                      <span className="ml-1">If an incident/event happens in this Location, only the Leaders of the Secondary Department will receive notifications.</span>
                    </div>
                  </div>
                  {/* Arrow pointing up */}
                  <div className="absolute -top-2 left-4 w-0 h-0 border-l-4 border-r-4 border-b-4 border-l-transparent border-r-transparent border-b-black"></div>
                </div>
              </div>
            </div>
            <p className="text-sm text-gray-600">{selectedZone.zone_name}</p>
          </div>
          <button
            onClick={onBack}
            className="text-gray-400 hover:text-gray-600 p-2 rounded-full hover:bg-gray-100"
            title="Close (Esc)"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>
      
      {/* Add Department Button - Like "Add User to Department" */}
      <div className="mb-6">
        <button
          onClick={() => setShowAddDepartment(!showAddDepartment)}
          className="w-full px-4 py-3 bg-green-600 text-white rounded hover:bg-green-700 flex items-center justify-center font-medium"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Department to Location
        </button>
      </div>

      {/* Add Department Section - Collapsible */}
      {showAddDepartment && (
        <div className="mb-6 p-4 bg-gray-50 rounded border">
          <div className="relative">
            <Input
              placeholder="Search departments by name..."
              value={departmentSearchQuery}
              onChange={(e) => setDepartmentSearchQuery(e.target.value)}
              className="w-full"
            />
            {isSearching && (
              <div className="absolute right-3 top-3">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-600"></div>
              </div>
            )}
          </div>
          
          {/* Search Results */}
          {searchResults.length > 0 && (
            <div className="mt-3 space-y-2 max-h-40 overflow-y-auto">
              {searchResults.map(dept => (
                <div key={dept.id} className="flex items-center justify-between p-3 bg-white rounded border hover:bg-gray-50">
                  <span className="font-medium">{dept.department_name}</span>
                  <button
                    onClick={() => {
                      addDepartmentToLocation(dept.id, dept.department_name);
                      setShowAddDepartment(false); // Close after adding
                    }}
                    disabled={isAddingDepartment}
                    className="px-3 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700 disabled:bg-gray-400"
                  >
                    {isAddingDepartment ? 'Adding...' : 'Add'}
                  </button>
                </div>
              ))}
            </div>
          )}
          
          {departmentSearchQuery.trim() && searchResults.length === 0 && !isSearching && (
            <div className="mt-3 text-center text-gray-500 text-sm">
              No departments found matching "{departmentSearchQuery}"
            </div>
          )}
        </div>
      )}
      
      {/* Associated Departments List */}
      <div className="flex-1 overflow-y-auto">
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          Departments ({isLoadingDepartments ? '...' : associatedDepartments.length})
        </h3>
        
        {isLoadingDepartments ? (
          <div className="text-center py-12 text-gray-500">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-3"></div>
            <p className="text-lg font-medium">Loading departments...</p>
            <p className="text-sm text-gray-400 mt-1">Please wait while we fetch the associated departments.</p>
          </div>
        ) : associatedDepartments.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <Building className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p className="text-lg font-medium">No departments associated yet</p>
            <p className="text-sm text-gray-400 mt-1">Click "Add Department to Location" above to get started.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {associatedDepartments.map(dept => (
              <InlineDepartmentRow
                key={dept.id}
                department={dept}
                onRemove={() => handleRemoveDepartment(dept.id, dept.department_name)}
                onTogglePrimary={handleTogglePrimary}
                onNavigateToEdit={() => console.log('Navigate to edit', dept.id)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default LocationDepartmentManagement;